import React, { useContext, useEffect } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
	Badge,
	Button,
	IconButton,
	alpha,
	List,
	ListItem,
	ListItemIcon,
	ListItemSecondaryAction,
	ListItemText,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material';
import NavBreadcrumbs from './NavBreadcrumbs';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { resetStore, useAppDispatch, useAppSelector } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import {
	AccountCircleTwoTone,
	HelpTwoTone,
	LogoutTwoTone,
	SettingsTwoTone,
	SupportAgentTwoTone,
	SupervisorAccount,
	VerifiedOutlined
} from '@mui/icons-material';
import ProfilePicture from '../ui/atoms/ProfilePicture';
import { useTranslation } from 'react-i18next';
import { useBasket } from '../../redux/BasketContext';
import { getActiveSubscriptions } from '../../redux/slices/user';
import { currencyTypes, useCurrency } from '../../hooks/useCurrency';
import { RoleName, StripeIdentityVerificationStatus } from '../../types/user';
import { useTutorial } from '../ui/organisms/TutorialContext';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';
import TourOutlinedIcon from '@mui/icons-material/TourOutlined';
import { CustomThemeContext } from '../../themes/ThemeContext';

const TopToolbar = ({ height, showBreadCrumb = true }: { height?: string; showBreadCrumb?: boolean }) => {
	const { basket, clearBasket, basketEl, setBasketEl } = useBasket();
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { tourActive, setTourActive } = useTutorial();
	const { currencyCode } = useCurrency();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const { userId } = useAuthenticatedUser();
	const [currency, setCurrency] = React.useState<string>('');
	const { isDark } = useContext(CustomThemeContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [current, organisation, userGrants] = useAppSelector((state) => [
		state.user.currentUser,
		state.organisation.current,
		state.user?.userGrantsList
	]);

	useEffect(() => {
		const openBasket = () => {
			const basketIcon = document.querySelector('#top-toolbar-basket-icon') as SVGSVGElement | null;
			if (basketIcon) setBasketEl(basketIcon);
		};

		window.addEventListener('openBasketMenu', openBasket);
		return () => window.removeEventListener('openBasketMenu', openBasket);
	}, [setBasketEl]);

	const isPersonalPurchase = sessionStorage.getItem('Inspirus_catalog_identifier') ?? '';

	React.useEffect(() => {
		if (isPersonalPurchase === 'personal') {
			setCurrency(currencyTypes[currencyCode as keyof typeof currencyTypes]);
		} else {
			setCurrency(currencyTypes[organisation?.currency as keyof typeof currencyTypes]);
		}
	}, [organisation?.currency, currencyCode, isPersonalPurchase]);

	React.useEffect(() => {
		dispatch(
			getActiveSubscriptions({
				limit: 1
			})
		);
	}, [dispatch]);

	const appTourToggle = (
		<Tooltip title={tourActive ? 'Turn off App Tour' : 'Start App Tour'}>
			<IconButton
				size="small"
				onClick={() => setTourActive(!tourActive)}
				aria-label="Toggle app tour"
				aria-pressed={tourActive}
				sx={{
					color: tourActive ? 'primary.main' : 'text.secondary',
					backgroundColor: tourActive ? (t) => alpha(t.palette.primary.main, 0.12) : 'transparent',
					'&:hover': {
						backgroundColor: tourActive ? (t) => alpha(t.palette.primary.main, 0.2) : 'action.hover'
					}
				}}
			>
				<TourOutlinedIcon fontSize="small" />
			</IconButton>
		</Tooltip>
	);

	// noop — basket context loaded via useBasket hook
	const updateBasketContext = () => {
		/* noop */
	};

	React.useEffect(() => {
		updateBasketContext();
	}, [sessionStorage.getItem('Inspirus_catalog_identifier')]);

	const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		window.dispatchEvent(new CustomEvent('ProfileIconMenuOpened'));
	};

	const handleBasket = (event: React.MouseEvent<SVGSVGElement>) => {
		updateBasketContext();
		setBasketEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleBasketClose = () => {
		setBasketEl(null);
	};

	const { signOut } = useAuthenticator((context) => [context.user]);

	const logout = () => {
		// Store logout timestamp when user manually logs out
		if (userId) {
			const lastLogoutKey = `lastLogout_${userId}`;
			const currentTime = new Date().toISOString();
			localStorage.setItem(lastLogoutKey, currentTime);
		}

		signOut();
		resetStore();
		sessionStorage.clear();
		localStorage.clear();
	};

	const profilePicture = useAppSelector((state) => state.user.currentUser?.profilePicture);
	const agentEnabled = useAppSelector((state) => state.featureFlags?.flags?.['agent-enabled'] === true);
	const faqEnabled = useAppSelector((state) => state.featureFlags?.flags?.['faq-enabled'] === true);
	const appTourEnabled = useAppSelector((state) => state.featureFlags?.flags?.['app-tour-enabled'] !== false);

	const navigateToAgent = () => {
		navigate('/agent/');
	};

	const navigateToPreferences = () => {
		navigate('/preferences');
		handleClose();
	};

	const navigateToFaqs = () => {
		navigate('/faq');
		handleClose();
	};

	const handleCheckout = () => {
		setBasketEl(null);
		const isPersonalPurchase = sessionStorage.getItem('Inspirus_catalog_identifier') === 'personal';
		if (!isPersonalPurchase) {
			navigate(`/organisations/${organisation?.id}/billing-administrator/product-catalog/checkout`);
		} else {
			navigate(`/product-catalog/${current?.id}/checkout`);
		}
	};

	const navigate = useNavigate();

	const hideTopToolbar =
		userGrants &&
		(userGrants?.personal?.onboarding === false || userGrants?.personal?.onboarding === null) &&
		current?.roles.length === 0;

	return (
		<Box
			sx={{
				height,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				width: { md: '100%' }
			}}
		>
			{showBreadCrumb && <NavBreadcrumbs />}
			{/* {bannerMessage && (
				<Box sx={{
					display: closeBanner ? 'none' : 'flex',
					mt: 2
				}}>
					<Banner
						message={bannerMessage}
						severity={bannerSeverity}
						onClose={() => {
							setCloseBanner(true);
							storageUtils.setItem('sessionStorage', 'closeBanner', true);
						}}
					/>
				</Box>
			)} */}
			<Box
				display="flex"
				flexDirection="row"
				alignItems="center"
				gap={isMobile ? 1 : 2}
				sx={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}
			>
				{current?.stripeIdentityVerified === StripeIdentityVerificationStatus.Verified && (
					<Tooltip title="User Verified their identification with Stripe">
						<IconButton>
							<VerifiedOutlined
								sx={{
									color: 'rgb(46, 125, 50)'
								}}
							/>
						</IconButton>
					</Tooltip>
				)}

				{appTourEnabled && appTourToggle}

				{/* Basket icon with badge */}
				{!hideTopToolbar && (
					<Badge badgeContent={basket?.length || 0}>
						<Box
							sx={{
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'transform 0.2s',
								'&:hover': {
									transform: 'scale(1.05)'
								}
							}}
						>
							<svg
								id="top-toolbar-basket-icon"
								width="22"
								height="22"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								onClick={handleBasket}
								style={{ cursor: 'pointer' }}
								onMouseDown={(e) => {
									e.currentTarget.style.transform = 'scale(0.92)';
								}}
								onMouseUp={(e) => {
									e.currentTarget.style.transform = 'scale(1)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.transform = 'scale(1)';
								}}
							>
								<g clipPath="url(#clip0_358_1703)">
									<path
										d="M14.6389 4.96625C16.0707 5.03881 17.9944 4.52497 18.5926 6.26476C19.1276 10.1991 19.5106 14.163 20 18.1066C19.9578 19.2086 19.1537 19.9165 18.0796 19.9865H1.87561C0.795622 19.9063 -0.0228051 19.1335 -2.41293e-05 18.0231L1.31283 6.67904C1.3913 5.78889 2.13886 5.06243 3.02141 4.97046C3.76137 4.89284 4.60679 5.01687 5.35772 4.9654C5.47585 3.46439 5.86734 1.83935 7.13295 0.897733C8.81621 -0.354376 11.4723 -0.296158 13.0779 1.06226C14.1621 1.97941 14.5325 3.60276 14.638 4.9654L14.6389 4.96625ZM13.1201 4.9654C13.0214 4.25919 12.8881 3.48548 12.559 2.84762C11.6993 1.1787 8.31419 1.1517 7.45104 2.81977C7.11439 3.46945 6.97939 4.24738 6.87645 4.96625H13.1201V4.9654ZM17.0739 6.62251C17.0469 6.5896 16.9397 6.54319 16.8933 6.52969C12.3076 6.4546 7.68813 6.4546 3.10325 6.52969L2.94125 6.60901L1.60055 18.2788L1.74061 18.3918L18.0788 18.4256C18.2239 18.434 18.3606 18.3513 18.4264 18.2248L17.165 6.93216C17.1355 6.84356 17.1304 6.69254 17.073 6.62251H17.0739Z"
										fill={isDark ? '#fff' : '#002868'}
									/>
								</g>
								<defs>
									<clipPath id="clip0_358_1703">
										<rect width="20" height="19.9865" fill="white" />
									</clipPath>
								</defs>
							</svg>
						</Box>
					</Badge>
				)}
				{/* <Badge badgeContent={userActiveSubscriptionsCount} color="warning"> */}
				<ProfilePicture
					sx={{ ml: 0.5 }}
					image={profilePicture}
					aria-label="account of current user"
					aria-controls="menu-appbar"
					aria-haspopup="true"
					className="profile-icon-btn"
					onClick={handleMenu}
				/>
				{/* </Badge> */}
			</Box>

			{/* Profile Menu */}

			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				keepMounted
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				sx={{ marginTop: '2.5rem' }}
			>
				{!hideTopToolbar && [
					<MenuItem
						key="profile"
						className="profile-menu-item"
						onClick={() => {
							handleClose();
							navigate('/profile');
						}}
					>
						<ListItemIcon>
							<AccountCircleTwoTone fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t('pages.profile.title')}</ListItemText>
					</MenuItem>,
					<MenuItem
						key="account"
						className="my-account-menu-item"
						onClick={() => {
							handleClose();
							navigate('/account');
						}}
					>
						<ListItemIcon>
							{/* <Badge badgeContent={userActiveSubscriptionsCount} color="warning"> */}
							<SupervisorAccount fontSize="small" />
							{/* </Badge> */}
						</ListItemIcon>
						<ListItemText>{t('pages.profile.account')}</ListItemText>
					</MenuItem>,

					<MenuItem key="preferences" onClick={navigateToPreferences}>
						<ListItemIcon>
							<SettingsTwoTone fontSize="small" />
						</ListItemIcon>
						<ListItemText>{t('pages.wallet.preferences.title')}</ListItemText>
					</MenuItem>,
					agentEnabled ? (
						<MenuItem key="agent" onClick={navigateToAgent}>
							<ListItemIcon>
								<SupportAgentTwoTone fontSize="small" />
							</ListItemIcon>
							<ListItemText>{t('pages.agent.title')}</ListItemText>
						</MenuItem>
					) : null,
					faqEnabled ? (
						<MenuItem key="faq" className="faq-menu-item" onClick={navigateToFaqs}>
							<ListItemIcon>
								<HelpTwoTone fontSize="small" />
							</ListItemIcon>
							<ListItemText>F.A.Q.</ListItemText>
						</MenuItem>
					) : null
				]}

				<MenuItem onClick={logout}>
					<ListItemIcon>
						<LogoutTwoTone fontSize="small" />
					</ListItemIcon>
					<ListItemText>Logout</ListItemText>
				</MenuItem>
			</Menu>

			{/* Basket Display */}

			<Menu
				id="basket-menu"
				anchorEl={basketEl}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				keepMounted
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right'
				}}
				open={Boolean(basketEl)}
				onClose={handleBasketClose}
				sx={{
					marginTop: '1.5rem',
					'& .MuiPaper-root': {
						borderRadius: '12px',
						boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
						minWidth: '360px',
						maxWidth: '400px',
						overflow: 'hidden'
					}
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						borderBottom: '1px solid rgba(0,0,0,0.08)',
						p: '12px 16px'
					}}
				>
					<Typography variant="h6" fontWeight={600} fontSize="18px">
						Your Basket
					</Typography>
					<Typography variant="subtitle1" color="primary.main" fontWeight={600}>
						{basket?.length || 0} item{basket?.length !== 1 ? 's' : ''}
					</Typography>
				</Box>

				{basket?.length > 0 ? (
					<Box>
						<List
							sx={{
								maxHeight: '280px',
								overflowY: 'auto',
								padding: 0,
								width: '100%',
								'& .MuiListItem-root': {
									px: 2,
									borderBottom: '1px solid rgba(0,0,0,0.04)',
									py: 1.5
								},
								'&::-webkit-scrollbar': {
									width: '6px'
								},
								'&::-webkit-scrollbar-thumb': {
									backgroundColor: 'rgba(0,0,0,0.2)',
									borderRadius: '6px'
								}
							}}
						>
							{basket?.map((product) => (
								<ListItem key={product.id}>
									<ListItemText
										primary={
											<Typography fontWeight={500} fontSize="14px" sx={{ pr: 1 }}>
												{product.name}
											</Typography>
										}
										secondary={
											product.type?.toLowerCase() === 'license' ? (
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ display: 'block', mt: 0.5 }}
												>
													License
												</Typography>
											) : (
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ display: 'block', mt: 0.5 }}
												>
													Feature
												</Typography>
											)
										}
									/>
									<ListItemSecondaryAction>
										<Typography color="primary.main" fontWeight={600} fontSize="14px">
											{currency}
											{product.price}
										</Typography>
									</ListItemSecondaryAction>
								</ListItem>
							))}
						</List>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								padding: '12px 16px',
								borderTop: '1px solid rgba(0,0,0,0.08)',
								bgcolor: 'rgba(0,0,0,0.01)'
							}}
						>
							<Typography fontWeight={600}>Total</Typography>
							<Typography fontWeight={600} color="primary.main">
								{currency}
								{basket.reduce((acc, product) => acc + Number(product.price), 0).toFixed(2)}
							</Typography>
						</Box>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								padding: '12px 16px',
								gap: 1.5
							}}
						>
							<Button
								variant="outlined"
								color="error"
								onClick={() => {
									clearBasket();
									updateBasketContext();
								}}
								sx={{
									textTransform: 'none',
									borderRadius: '8px',
									py: 0.75,
									fontWeight: 500
								}}
							>
								Clear Basket
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleCheckout}
								sx={{
									textTransform: 'none',
									borderRadius: '8px',
									py: 0.75,
									fontWeight: 500
								}}
							>
								Checkout
							</Button>
						</Box>
					</Box>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2 }}>
						<svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M21 5L19 12H7.37671M20 16H8L6 3H3M9 20C9 20.5523 8.55228 21 8 21C7.44772 21 7 20.5523 7 20C7 19.4477 7.44772 19 8 19C8.55228 19 9 19.4477 9 20ZM20 20C20 20.5523 19.5523 21 19 21C18.4477 21 18 20.5523 18 20C18 19.4477 18.4477 19 19 19C19.5523 19 20 19.4477 20 20Z"
								stroke="#9e9e9e"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<Typography color="text.secondary" fontSize="14px" align="center">
							Your basket is empty
						</Typography>
						{/* eslint-disable no-mixed-spaces-and-tabs */}
						<Box display={'flex'} flexDirection={'row'} gap={1}>
							{/*<Button*/}
							{/*	variant="outlined"*/}
							{/*	color="primary"*/}
							{/*	onClick={() => {*/}
							{/*		handleBasketClose();*/}
							{/*		isPersonalPurchase === 'personal'*/}
							{/*			? navigate(`/product-catalog/${current?.id}/checkout`)*/}
							{/*			: navigate(*/}
							{/*				`/organisations/${organisation?.id}/billing-administrator/product-catalog/checkout`*/}
							{/*			  );*/}
							{/*	}}*/}
							{/*	sx={{*/}
							{/*		textTransform: 'none',*/}
							{/*		borderRadius: '8px',*/}
							{/*		mt: 1,*/}
							{/*		fontWeight: 500*/}
							{/*	}}*/}
							{/*>*/}
							{/*	View Basket*/}
							{/*</Button>*/}
							<Button
								variant="outlined"
								color="primary"
								onClick={() => {
									handleBasketClose();
									isPersonalPurchase === 'personal' ||
									!(
										current?.roles &&
										current?.roles?.filter(
											(role) => role.roleName === RoleName.BillingAdministrator
										)?.length > 0
									)
										? navigate('/product-catalog')
										: navigate(
												`/organisations/${organisation?.id}/billing-administrator/product-catalog`
										  );
								}}
								sx={{
									textTransform: 'none',
									borderRadius: '8px',
									mt: 1,
									fontWeight: 500
								}}
							>
								Browse Products
							</Button>
						</Box>
						{/* eslint-enable no-mixed-spaces-and-tabs */}
					</Box>
				)}
			</Menu>
		</Box>
	);
};

export default TopToolbar;
